import * as YupDefault from 'yup';
import {camelCase} from 'lodash-es';

/**
 * Re-export the Yup object schema from the yup module.
 */
export const Yup = YupDefault;

/**
 * Extends the Yup object schema to include a custom method to compare properties in a collection.
 */
declare module 'yup' {
    interface ObjectSchema<TShape> {
        comparePropertiesInCollection(
            message: string,
            comparator: (value: TShape, item: TShape) => boolean
        ): this;
    }
}

/**
 * Adds a custom Yup schema validator to compare the value of a property
 * in a collection against another property in the same collection.
 *
 * @param message - The error message to display if validation fails
 * @param comparator - The comparison function to validate items in the collection
 */
function comparePropertiesInCollection<TShape>(
    message: string,
    comparator: (value: TShape, item: TShape) => boolean
) {
    return this.test(
        'compare-properties-in-collection',
        message,
        (value, context) => {
            const parentCollection = context.parent;

            if (!Array.isArray(parentCollection)) return true;

            const isValid: boolean = parentCollection.some((item) =>
                comparator(value, item)
            );

            return !isValid;
        }
    );
}

/**
 * TransformYupErrorsIntoObject
 *
 * @description Transform the useless yup error into a usable validation object
 * @param {YupDefault.ValidationError} errors Yup validation errors
 * @returns {Record<string, string | Record<number, Record<string, string>>>} Validation errors
 */
export const transformYupErrorsIntoObject = (
    errors: YupDefault.ValidationError
): Record<string, string | Record<number, Record<string, string>>> => {
    const validationErrors: Record<
        string,
        string | Record<number, Record<string, string>>
    > = {};

    errors.inner.forEach((error: YupDefault.ValidationError, innerIndex) => {
        // eslint-disable-next-line no-eq-null,eqeqeq
        if (error.path != null) {
            // Check if the path contains array indexing like "[0].email"
            const arrayMatch = error.path.match(/^\[(\d+)\]\.(.+)$/);

            if (arrayMatch) {
                const index = parseInt(arrayMatch[1], 10);
                const field = arrayMatch[2];

                // Initialize the index object if it doesn't exist
                if (
                    !validationErrors[index] ||
                    typeof validationErrors[index] === 'string'
                ) {
                    validationErrors[index] = {};
                }

                // Now we can safely access the nested object
                const indexedErrors = validationErrors[
                    index
                ] as unknown as Record<string, string>;

                indexedErrors[field] = error.errors[0];
            } else {
                const errorName = error?.type
                    ? camelCase(error.type)
                    : innerIndex;

                // Handle non-indexed errors normally
                validationErrors[errorName] = error.errors[0];
            }
        }
    });

    return validationErrors;
};

Yup.addMethod(
    Yup.object,
    'comparePropertiesInCollection',
    comparePropertiesInCollection
);
